import RequestData from "./RequestData";
import { appEnvironment } from "../../ApplicationContext";
import { ExtendedErrorResponse } from "../../types/ErrorResponse";

export async function callGetApi<ResponseType>(path: string, token: string): Promise<ResponseType> {
    const baseUrl = appEnvironment.REACT_APP_SCHEMA_BASEURL_V3;

    const response = await fetch(`${baseUrl}${path}`, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
    });

    if (response.ok) {
        const data = await response.json();
        return data as ResponseType;
    }

    let potentialJsonError = {};

    try {
        // In scenarios of a failure, the response body might still be a JSON object
        // (e.g. AUTH-006) although it most likely will be an empty response. In the event
        // that the response is a valid JSON object, we will simply pass that on through
        // the extended error response so that it can potentially be handled by the caller.
        potentialJsonError = await response.json();
    } catch (_) {
        // Response body is not a JSON object, do nothing about it.
    }

    const error: ExtendedErrorResponse = {
        status: response.status,
        statusText: response.statusText,
        error: potentialJsonError,
    };

    throw error;
}

/**
 * Reads the JSON body of a successful response, or 'undefined' when there is
 * none. A 204 never carries a body and a 201 need not, so parsing unconditionally
 * would turn a successful call into a 'SyntaxError'.
 */
async function parseJsonBody<ResponseType>(response: Response): Promise<ResponseType | undefined> {
    if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined;
    }

    try {
        return (await response.json()) as ResponseType;
    } catch (_) {
        // Body is absent or not JSON. Covers responses that omit 'content-length'.
        return undefined;
    }
}

/**
 * Throws for a response that is not 'ok'. A JSON body is thrown as-is, since
 * callers read the service's own shapes off it. When there is no JSON body the
 * status is thrown instead of the 'SyntaxError' from 'response.json()', which
 * would otherwise reach the user as "Unexpected end of JSON input".
 */
async function throwResponseError(response: Response): Promise<never> {
    let potentialJsonError: unknown = undefined;

    try {
        potentialJsonError = await response.json();
    } catch (_) {
        // Response body is not a JSON object, do nothing about it.
    }

    if (potentialJsonError && Object.keys(potentialJsonError).length > 0) {
        throw potentialJsonError;
    }

    const error: ExtendedErrorResponse = {
        status: response.status,
        statusText: response.statusText,
        error: {},
    };

    throw error;
}

export async function callPostApi<PayloadType, ResponseType>(
    path: string,
    token: string,
    payload: PayloadType
): Promise<ResponseType | undefined> {
    const baseUrl = appEnvironment.REACT_APP_SCHEMA_BASEURL_V3;

    const response = await fetch(`${baseUrl}${path}`, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        await throwResponseError(response);
    }

    return await parseJsonBody<ResponseType>(response);
}

export async function callPutApi(path: string, token: string, requestBody: RequestData): Promise<Response | undefined> {
    const baseUrl = appEnvironment.REACT_APP_SCHEMA_BASEURL_V3;

    const response = await fetch(`${baseUrl}${path}`, {
        method: "PUT",
        mode: "cors",
        credentials: "omit",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        await throwResponseError(response);
    }

    return await parseJsonBody<Response>(response);
}

export async function callPatchApi(
    path: string,
    token: string,
    requestBody: RequestData
): Promise<Response | undefined> {
    const baseUrl = appEnvironment.REACT_APP_SCHEMA_BASEURL_V3;

    const response = await fetch(`${baseUrl}${path}`, {
        method: "PATCH",
        mode: "cors",
        credentials: "omit",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        await throwResponseError(response);
    }

    return await parseJsonBody<Response>(response);
}
